import React, { useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import EditIcon from '../icons/EditIcon';
import { Estimate } from '../../pages/EstimatesPage';
import { updateEstimate } from '../../services/estimateService';
import { useEstimateData } from '../../contexts/EstimateDataContext';
import EstimateSettingsDrawer from './EstimateSettingsDrawer';

interface EstimateInfoTabProps {
  estimate: Estimate;
  onRefresh?: () => void;
  onBack?: () => void;
}

const toFormNum = (v: unknown): string => {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : '';
};

const EstimateInfoTab: React.FC<EstimateInfoTabProps> = ({ estimate, onRefresh, onBack }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: estimate.name,
    client: estimate.client,
    clientAddress: (estimate as any).clientAddress || (estimate as any).client_address || '',
    objectName: estimate.objectName,
    objectAddress: (estimate as any).objectAddress || (estimate as any).object_address || '',
    kz: toFormNum(estimate.kz),
    kp: toFormNum(estimate.kp),
    z: toFormNum(estimate.z),
    rg: toFormNum(estimate.rg),
    vat: toFormNum(estimate.vat),
  });

  const handleEdit = () => {
    setIsEditing(true);
  };

  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateEstimate(estimate.id, {
        name: formData.name,
        object_name: formData.objectName,
        client: formData.client,
        client_address: formData.clientAddress,
        object_address: formData.objectAddress,
        markup_kz: parseFloat(formData.kz) || 0,
        markup_kp: parseFloat(formData.kp) || 0,
        markup_z: parseFloat(formData.z) || 0,
        vat: parseFloat(formData.vat) || 0,
        rg: parseFloat(formData.rg) || 0
      } as any);
      await (onRefresh?.() ?? Promise.resolve());
      setIsEditing(false);
    } catch (err) {
      console.error('Błąd zapisu kosztorysu:', err);
      setSaveError('Nie udało się zapisać. Sprawdź konsolę.');
    }
  };

  const handleCancel = () => {
    setFormData({
      name: estimate.name,
      client: estimate.client,
      clientAddress: (estimate as any).clientAddress || (estimate as any).client_address || '',
      objectName: estimate.objectName,
      objectAddress: (estimate as any).objectAddress || (estimate as any).object_address || '',
      kz: toFormNum(estimate.kz),
      kp: toFormNum(estimate.kp),
      z: toFormNum(estimate.z),
      rg: toFormNum(estimate.rg),
      vat: toFormNum(estimate.vat),
    });
    setIsEditing(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // gdy dostaniemy odświeżony nagłówek z rodzica – uaktualnij formularz (o ile nie edytujemy)
  React.useEffect(()=>{
    if(!isEditing){
      setFormData({
        name: estimate.name,
        client: estimate.client,
        clientAddress: (estimate as any).clientAddress || (estimate as any).client_address || '',
        objectName: estimate.objectName,
        objectAddress: (estimate as any).objectAddress || (estimate as any).object_address || '',
        kz: toFormNum(estimate.kz),
        kp: toFormNum(estimate.kp),
        z: toFormNum(estimate.z),
        rg: toFormNum(estimate.rg),
        vat: toFormNum(estimate.vat),
      });
    }
  }, [estimate, isEditing]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'u' || e.key === 'U') && !isEditing && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        onBack && onBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, onBack]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', position: 'relative', overflow: 'visible' }}>
      {/* Wróć do kosztorysów – nad kontenerem, bez przesuwania układu */}
      <Box
        onClick={() => onBack?.()}
        sx={{
          position: 'absolute',
          top: '-24px',
          left: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        aria-label="Wróć do kosztorysów"
        role="button"
      >
        <KeyboardArrowDown sx={{ transform: 'rotate(90deg)', width: 16, height: 16, color: '#1F1FB0' }} />
        <Typography
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            lineHeight: '16px',
            fontWeight: 500,
            color: '#1F1FB0'
          }}
        >
          Wróć do kosztorysów
        </Typography>
      </Box>
      {/* Wiersz nagłówka zgodny z pozycją nagłówka na stronie projektów */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0, mb: 0 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0
          }}
        >
          {`${estimate.estimateNumber || estimate.id} - ${estimate.client}`.trim()}
        </Typography>
      </Box>

      {/* Główny kontener z sekcjami */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mt: '28px' }}>
        {/* Jedna ramka z obiema sekcjami */}
        <Box sx={{ position: 'relative', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '4px', padding: '16px', minHeight: '468px' }}>
          {/* Przyciski Ustawienia i Edycja w prawym górnym rogu */}
          <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', gap: 0, zIndex: 1 }}>
            <Tooltip title="Ustawienia kosztorysu">
              <IconButton
                onClick={() => setSettingsOpen(true)}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                }}
              >
                <Box
                  component="img"
                  src="/assets/icons/settings_24dp_black.svg"
                  alt="Ustawienia"
                  sx={{ width: 24, height: 24, color: '#2D2D32' }}
                />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={handleEdit}
              sx={{
                width: '32px',
                height: '32px',
                borderRadius: '4px',
              }}
            >
              <EditIcon sx={{ width: 24, height: 24, color: '#2D2D32' }} />
            </IconButton>
          </Box>
          <EstimateSettingsDrawer
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            estimate={estimate}
            onSave={onRefresh}
          />

          {/* Sekcja: Podstawowe informacje */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', mb: '24px' }}>
            {/* Tytuł sekcji */}
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#2D2D32',
                mb: '16px'
              }}
            >
              Podstawowe informacje
            </Typography>

            {/* Formularze w układzie poziomym */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Pierwszy rząd: Nazwa Kosztorysu i Zamawiający */}
              <Box sx={{ display: 'flex', gap: '24px' }}>
              <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Nazwa Kosztorysu</Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={!isEditing}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      '& fieldset': { borderColor: '#d4d4d9' },
                      '&:hover fieldset': { borderColor: '#595966' },
                      '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                      '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: formData.name ? '#2D2D32' : '#B9B9B9'
                    }
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Zamawiający</Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={formData.client}
                  onChange={(e) => handleInputChange('client', e.target.value)}
                  disabled={!isEditing}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      backgroundColor: '#fff',
                      borderRadius: '4px',
                      '& fieldset': { borderColor: '#d4d4d9' },
                      '&:hover fieldset': { borderColor: '#595966' },
                      '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                      '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: 'Roboto, sans-serif',
                      fontSize: '14px',
                      color: formData.client ? '#2D2D32' : '#B9B9B9'
                    }
                  }}
                />
              </Box>
              </Box>

              {/* Adres Zamawiającego */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Adres Zamawiającego</Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.clientAddress}
                onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.clientAddress ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>

              {/* Nazwa Obiektu */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Nazwa Obiektu</Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.objectName}
                onChange={(e) => handleInputChange('objectName', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.objectName ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>

              {/* Adres Obiektu */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Adres Obiektu</Typography>
              <TextField
                size="small"
                fullWidth
                value={formData.objectAddress}
                onChange={(e) => handleInputChange('objectAddress', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.objectAddress ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
            </Box>
          </Box>

          {/* Sekcja: Narzuty kosztorysu */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Tytuł sekcji */}
            <Typography
              sx={{
                fontFamily: 'Roboto, sans-serif',
                fontSize: '16px',
                fontWeight: 400,
                color: '#2D2D32',
                mb: '16px'
              }}
            >
              Narzuty kosztorysu
            </Typography>

            {/* Formularze w układzie poziomym */}
            <Box sx={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 0', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Kz - Koszty zakupu (%)</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.kz}
                onChange={(e) => handleInputChange('kz', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.kz ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Kp - Koszty pośrednie (%)</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.kp}
                onChange={(e) => handleInputChange('kp', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.kp ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Z - Zysk (%)</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.z}
                onChange={(e) => handleInputChange('z', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.z ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Rg - Roboczo-godzina (zł/h)</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.rg}
                onChange={(e) => handleInputChange('rg', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.rg ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
              <Box sx={{ flex: '1 1 0', minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>VAT (%)</Typography>
              <TextField
                size="small"
                fullWidth
                type="number"
                value={formData.vat}
                onChange={(e) => handleInputChange('vat', e.target.value)}
                disabled={!isEditing}
                variant="outlined"
                inputProps={{ step: 0.1, min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '4px',
                    '& fieldset': { borderColor: '#d4d4d9' },
                    '&:hover fieldset': { borderColor: '#595966' },
                    '&.Mui-focused fieldset': { borderColor: '#595966 !important' },
                    '&.Mui-focused': { boxShadow: 'none', outline: 'none' }
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    color: formData.vat ? '#2D2D32' : '#B9B9B9'
                  }
                }}
              />
              </Box>
            </Box>
          </Box>

          {/* Błąd zapisu */}
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          {/* Przyciski Anuluj i Zapisz */}
          <Box sx={{ display: 'flex', gap: '16px', mt: '16px', justifyContent: 'flex-end', minHeight: '40px' }}>
            {isEditing && (
              <>
                <Button
                  onClick={handleCancel}
                  sx={{
                    width: '72px',
                    height: '40px',
                    borderRadius: '4px',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#020242',
                    textTransform: 'none',
                    border: 'none',
                    backgroundColor: 'transparent',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Anuluj
                </Button>
                <Button
                  onClick={handleSave}
                  sx={{
                    width: '123px',
                    height: '40px',
                    borderRadius: '4px',
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: '#FFFFFF',
                    backgroundColor: '#3F3FDC',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3F3FDC'
                    }
                  }}
                >
                  Zapisz
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EstimateInfoTab;
